import React from 'react';
import {Button, ListItem, ListItemText, Stack} from '@mui/material';

import {TagChip, DateWithWeekday} from 'presentation/components';
import Icon from 'presentation/icons';
import {translations} from 'presentation/translations-new';
import {AppointmentBookingEnum} from 'main/analytics/gaTagsEnum';
import getFormattedHour from 'presentation/pages/private/booking/utils/getFormattedHour';
import {FavoriteDoctorsLikeButton} from 'presentation/modules/booking/favoriteDoctors/components/FavoriteDoctorsLikeButton';
import {formatText} from 'presentation/utils';

import {StyledCard, StyledStack} from './styles';
import {PreviousProviderCardProps} from './types';
import {usePreviousProviderCard} from './hooks/usePreviousProviderCard';

const {previousProvider, seeMoreSchedules, seeLessSchedules} =
	translations['pt-br'].privateFlow.bookingFlow.professionalFlow;

export function PreviousProviderCard({
	flowType,
	professionalCode,
	professionalName,
	dates,
	recommendations,
	onSelectHour,
	isFavorite,
}: PreviousProviderCardProps) {
	const {
		colors,
		specialtyCode,
		subSpecialtyCode,
		isExpanded,
		hasMultipleDates,
		visibleDates,
		showFavoriteButton,
		toggleExpanded,
	} = usePreviousProviderCard({dates, flowType});

	return (
		<ListItem disableGutters>
			<StyledCard elevation={0}>
				<Stack gap={1.5}>
					<TagChip
						label={previousProvider}
						bgColor={colors.light.primary['30Percent']}
						icon={
							<Icon name="time-default" size={18} color={colors.brand.primary.blue} />
						}
						sx={{px: 1, alignSelf: 'flex-start'}}
					/>

					<Stack flexDirection="row" alignItems="center" justifyContent="space-between">
						<ListItemText
							primary={formatText(professionalName)}
							slotProps={{
								primary: {variant: 'h6'},
							}}
						/>
						{showFavoriteButton ? (
							<FavoriteDoctorsLikeButton
								cdPrestador={Number(professionalCode)}
								cdEspecialidade={Number(specialtyCode)}
								cdSubEspecialidade={Number(subSpecialtyCode)}
								selected={isFavorite}
							/>
						) : null}
					</Stack>

					{visibleDates.map(({date, availableTimes}) => (
						<Stack key={date} gap={2}>
							<DateWithWeekday date={date} />
							<StyledStack direction="row" gap={1}>
								{availableTimes.map((hour, index) => (
									<Button
										key={`${hour}-${index}`}
										data-test="button-hour"
										variant="outlined"
										sx={{borderRadius: 0.5, fontSize: '1rem'}}
										data-tag-id={
											AppointmentBookingEnum.SELECIONOU_HORARIO_DISPONIVEL
										}
										onClick={() =>
											onSelectHour({
												hour,
												profCode: professionalCode,
												profName: professionalName,
												date,
												recommendations,
											})
										}>
										{getFormattedHour(hour, flowType)}
									</Button>
								))}
							</StyledStack>
						</Stack>
					))}

					{hasMultipleDates ? (
						<Stack alignItems="center">
							<Button
								variant="text"
								sx={{fontWeight: 400, fontSize: '1rem'}}
								endIcon={
									<Icon
										name={isExpanded ? 'arrow-drop-up' : 'arrow-drop-down'}
										size={24}
									/>
								}
								onClick={toggleExpanded}>
								{isExpanded ? seeLessSchedules : seeMoreSchedules}
							</Button>
						</Stack>
					) : null}
				</Stack>
			</StyledCard>
		</ListItem>
	);
}
